import React, { useState } from 'react';
import { useWeddingContext } from '../context/WeddingContext';
import { TaskCategory, TaskStatus, Assignee, Task } from '../types';
import { categories } from '../constants/categories';

const AddTask: React.FC = () => {
  const { addTask, setCurrentView } = useWeddingContext();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: categories[0],
    assignee: '',
    priority: '中',
    status: '未着手' as TaskStatus,
    dueDate: ''
  });
  
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'タスク名は必須です';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // 新しいタスクを作成
      const newTask: Omit<Task, 'id' | 'createdAt' | 'updatedAt'> = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category as TaskCategory,
        assignee: formData.assignee as Assignee,
        priority: formData.priority,
        status: formData.status,
        dueDate: formData.dueDate || ''
      };

      await addTask(newTask);
      
      // フォームをリセット
      setFormData({
        name: '',
        description: '',
        category: categories[0],
        assignee: '',
        priority: '中',
        status: '未着手' as TaskStatus,
        dueDate: ''
      });
      
      setErrors({});
      
      // タスクリストに戻る
      setCurrentView('tasks');
    } catch (error) {
      console.error('Failed to add task:', error);
      setErrors({ general: 'タスクの追加に失敗しました。もう一度お試しください。' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div className="pb-24 md:pb-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">📝 新しいタスクを追加</h2>
        <button
          onClick={() => setCurrentView('tasks')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition duration-150"
        >
          戻る
        </button>
      </div>

      {/* 全体エラーメッセージ */}
      {errors.general && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{errors.general}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* メインフォーム */}
        <div className="form-container">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">新しいタスクを作成</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* タスク名 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                タスク名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-pink-400 outline-none ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="例: ドレス試着の予約"
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            {/* 説明 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                説明
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none"
                rows={3}
                placeholder="タスクの詳細説明（任意）"
                disabled={isSubmitting}
              />
            </div>

            {/* カテゴリ・担当者 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  カテゴリ
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none"
                  disabled={isSubmitting}
                >
                  {categories.map((category: string) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  担当者
                </label>
                <select
                  value={formData.assignee}
                  onChange={(e) => handleChange('assignee', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none"
                  disabled={isSubmitting}
                >
                  <option value="">未設定</option>
                  <option value="新郎">新郎</option>
                  <option value="新婦">新婦</option>
                  <option value="二人">二人</option>
                </select>
              </div>
            </div>

            {/* 優先度・ステータス */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  優先度
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none"
                  disabled={isSubmitting}
                >
                  <option value="高">高</option>
                  <option value="中">中</option>
                  <option value="低">低</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ステータス
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none"
                  disabled={isSubmitting}
                >
                  <option value="未着手">未着手</option>
                  <option value="進行中">進行中</option>
                  <option value="完了">完了</option>
                </select>
              </div>
            </div>

            {/* 期限日 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                期限日
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleChange('dueDate', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none"
                disabled={isSubmitting}
              />
            </div>

            {/* 送信ボタン */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full font-semibold py-3 px-6 rounded-lg transition duration-150 ${
                isSubmitting 
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                  : 'bg-pink-500 hover:bg-pink-600 text-white'
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  追加中...
                </>
              ) : (
                <>
                  ➕ タスクを追加
                </>
              )}
            </button>
          </form>
        </div>

        {/* ヘルプ・ヒント */}
        <div className="space-y-6">
          <div className="bg-pink-50 border border-pink-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-pink-700 mb-3">
              💡 タスク作成のヒント
            </h3>
            <ul className="space-y-2 text-sm text-pink-600">
              <li className="flex items-start">
                <span className="text-pink-500 mr-2 mt-1">✓</span>
                具体的で分かりやすいタスク名をつけましょう
              </li>
              <li className="flex items-start">
                <span className="text-pink-500 mr-2 mt-1">✓</span>
                期限日を設定すると進捗管理がしやすくなります
              </li>
              <li className="flex items-start">
                <span className="text-pink-500 mr-2 mt-1">✓</span>
                担当者を明確にすることで責任の所在がはっきりします
              </li>
              <li className="flex items-start">
                <span className="text-pink-500 mr-2 mt-1">✓</span>
                優先度を適切に設定して効率的に進めましょう
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-700 mb-3">
              ✨ カテゴリ説明
            </h3>
            <div className="space-y-1 text-xs text-blue-600">
              <p><strong>情報収集・計画:</strong> 会場見学、予算計画など</p>
              <p><strong>式場・会場:</strong> 会場予約、装飾打ち合わせなど</p>
              <p><strong>衣装・美容:</strong> ドレス選び、ヘアメイクなど</p>
              <p><strong>招待状・ペーパーアイテム:</strong> 招待状作成、席次表など</p>
              <p><strong>料理・飲み物:</strong> メニュー選び、ケーキ選択など</p>
              <p><strong>音楽・演出:</strong> BGM選択、演出プランニングなど</p>
              <p><strong>写真・映像:</strong> フォトグラファー手配など</p>
              <p><strong>その他:</strong> 上記以外の項目</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTask; 